import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { toast } from '../components/toasts';
import { get, hasPermission } from '../lib/api';
import { callPartyLabel, contactLabel } from '../lib/format';
import { AttendanceItem, CallView, SyncTask } from '../lib/types';
import { loadNotificationLog, logNotification } from './notification-log';
import { desktopNotify, playSound } from './notifications';
import { useSession, useSocketEvent } from './session';
import { setTyping } from './typing';

type Data = Record<string, string | null | undefined>;

/** Atendimento aberto na tela agora (pela URL). */
const openAttendanceId = () => /^\/atendimento\/([0-9a-f-]{36})/.exec(window.location.pathname)?.[1] ?? null;
const watching = (attendanceId: string | null | undefined) =>
  Boolean(attendanceId) && attendanceId === openAttendanceId() && document.visibilityState === 'visible' && document.hasFocus();

/**
 * Avisos do servidor → dados da tela, som e notificações. Os avisos só trazem ids: a tela relê o
 * que mudou pela API (TanStack Query), que aplica as permissões.
 */
export function RealtimeBridge({ onOpenAttendance }: { onOpenAttendance: (id: string) => void }) {
  const qc = useQueryClient();
  const { user } = useSession();
  // Histórico de avisos é por usuário (troca no login/logout).
  useEffect(() => loadNotificationLog(user?.id ?? null), [user?.id]);

  /** Atendimento nas listas já carregadas (para o nome do contato na notificação). */
  const findCached = useCallback(
    (attendanceId: string): { item: AttendanceItem; list: string } | null => {
      for (const [key, data] of qc.getQueriesData<AttendanceItem[]>({ queryKey: ['attendances'] })) {
        const item = data?.find((a) => a.id === attendanceId);
        if (item) return { item, list: String(key[1]) };
      }
      return null;
    },
    [qc],
  );

  const attendanceChanged = useCallback(
    (d: Data) => {
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      void qc.invalidateQueries({ queryKey: ['attendance', d.attendanceId] });
      void qc.invalidateQueries({ queryKey: ['sync', d.attendanceId] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      if (d.change === 'created') {
        playSound('queue');
        logNotification({ kind: 'queue', title: 'Novo atendimento na fila', body: 'Um cliente está aguardando atendimento.', attendanceId: d.attendanceId });
        desktopNotify('Novo atendimento na fila', 'Um cliente está aguardando atendimento.', () => onOpenAttendance(d.attendanceId!), `queue-${d.attendanceId}`);
      }
    },
    [qc, onOpenAttendance],
  );
  const messageChanged = useCallback(
    (d: Data) => {
      void qc.invalidateQueries({ queryKey: ['messages', d.conversationId] });
      // Prévia e contador de não lidas da lista.
      void qc.invalidateQueries({ queryKey: ['attendances'] });
    },
    [qc],
  );
  /**
   * Nota interna de outro operador (quem ajuda ou o responsável) num atendimento em que eu trabalho:
   * avisa, para não passar despercebida entre as próprias notas. Menção já tem aviso próprio.
   */
  const noteFromColleague = useCallback(
    (d: Data) => {
      if (!d.attendanceId || !d.by || d.by === user?.id || watching(d.attendanceId)) return;
      const item = findCached(d.attendanceId)?.item;
      const works = item?.status === 'em_atendimento' && (item.operatorId === user?.id || item.participants?.some((p) => p.userId === user?.id));
      if (!works) return;
      const name = qc.getQueryData<Array<{ id: string; name: string }>>(['operators'])?.find((o) => o.id === d.by)?.name ?? 'Outro operador';
      playSound('message');
      logNotification({ kind: 'message', title: `Nota de ${name}`, body: `No atendimento de ${contactLabel(item!)}.`, attendanceId: d.attendanceId });
      desktopNotify(`Nota de ${name}`, `No atendimento de ${contactLabel(item!)}.`, () => onOpenAttendance(d.attendanceId!), `note-${d.attendanceId}`);
    },
    [qc, findCached, user?.id, onOpenAttendance],
  );
  /** Cliente digitando ou gravando áudio (só em memória; a mensagem que chega apaga). */
  const contactTyping = useCallback((d: Data) => {
    if (!d.conversationId) return;
    setTyping(d.conversationId, d.state === 'composing' || d.state === 'recording' ? d.state : null);
  }, []);
  const messageNew = useCallback(
    (d: Data) => {
      messageChanged(d);
      if (d.kind === 'inbound' && d.conversationId) setTyping(d.conversationId, null);
      if (d.kind === 'internal') return noteFromColleague(d);
      if (d.kind !== 'inbound' || !d.attendanceId || watching(d.attendanceId)) return;
      // Avisa quem está com o atendimento; mensagem de quem ainda está na fila também avisa.
      const cached = findCached(d.attendanceId);
      const isMine = cached?.item.operatorId === user?.id && cached?.item.status === 'em_atendimento';
      const inQueue = cached?.item.status === 'na_fila';
      if (!isMine && !inQueue) return;
      playSound('message');
      logNotification({
        kind: 'message',
        title: isMine ? `Mensagem de ${contactLabel(cached!.item)}` : 'Mensagem na fila',
        body: isMine ? 'Nova mensagem do cliente' : contactLabel(cached!.item),
        attendanceId: d.attendanceId,
      });
      desktopNotify(
        isMine ? `Mensagem de ${contactLabel(cached!.item)}` : 'Mensagem na fila',
        isMine ? 'Nova mensagem do cliente' : contactLabel(cached!.item),
        () => onOpenAttendance(d.attendanceId!),
        `msg-${d.attendanceId}`,
      );
    },
    [messageChanged, noteFromColleague, findCached, user?.id, onOpenAttendance],
  );
  const syncChanged = useCallback(
    (d: Data) => {
      void qc.invalidateQueries({ queryKey: ['sync', d.attendanceId] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['attendance', d.attendanceId] });
      if (d.status !== 'waiting' && d.status !== 'failed') {
        void qc.invalidateQueries({ queryKey: ['pending-sync'] });
        return;
      }
      void (async () => {
        // A supervisão é avisada de todas; o operador, só das pendências dos atendimentos dele.
        const tasks = await qc.fetchQuery({ queryKey: ['pending-sync'], queryFn: () => get<SyncTask[]>('/ticket-sync/tasks'), staleTime: 0 }).catch(() => []);
        if (!hasPermission(user, 'attendance:read_all') && !tasks.some((t) => t.id === d.taskId)) return;
        playSound('alert');
        logNotification({ kind: 'sync', title: 'Agidesk precisa de atenção', body: d.error ?? 'A sincronização do chamado parou', attendanceId: d.attendanceId });
        toast(`Agidesk: ${d.error ?? 'a sincronização precisa de atenção'}`, 'error', {
          label: 'Abrir',
          run: () => onOpenAttendance(d.attendanceId!),
        });
      })();
    },
    [qc, user, onOpenAttendance],
  );
  const presenceChanged = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['presence'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  }, [qc]);
  const transferredToMe = useCallback(
    (d: Data) => {
      playSound('alert');
      logNotification({ kind: 'transfer', title: 'Atendimento transferido para você', body: 'Abra para assumir.', attendanceId: d.attendanceId });
      toast('Um atendimento foi transferido para você', 'info', { label: 'Abrir', run: () => onOpenAttendance(d.attendanceId!) });
      desktopNotify('Atendimento transferido para você', 'Abra para assumir.', () => onOpenAttendance(d.attendanceId!));
    },
    [onOpenAttendance],
  );
  // Ligação atendida: abre na tela o atendimento em que o tempo da ligação está contando.
  const voiceStarted = useCallback(
    (d: Data) => {
      if (d.kind !== 'voice' || !d.attendanceId) return;
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      void qc.invalidateQueries({ queryKey: ['calls', 'active'] });
      onOpenAttendance(d.attendanceId);
    },
    [qc, onOpenAttendance],
  );

  // Ajuda: o responsável me chamou, ou alguém entrou no meu atendimento para ajudar.
  const helpRequested = useCallback(
    (d: Data) => {
      if (!d.attendanceId) return;
      playSound('alert');
      logNotification({ kind: 'help', title: 'Pediram sua ajuda', body: 'Você entrou num atendimento para ajudar.', attendanceId: d.attendanceId });
      toast('Pediram sua ajuda num atendimento', 'info', { label: 'Abrir', run: () => onOpenAttendance(d.attendanceId!) });
      desktopNotify('Pediram sua ajuda', 'Você entrou num atendimento para ajudar.', () => onOpenAttendance(d.attendanceId!), `help-${d.attendanceId}`);
    },
    [onOpenAttendance],
  );
  const helperJoined = useCallback(
    (d: Data) => {
      if (!d.attendanceId || d.userId === user?.id) return;
      const name = qc.getQueryData<Array<{ id: string; name: string }>>(['operators'])?.find((o) => o.id === d.userId)?.name ?? 'Outro operador';
      playSound('alert');
      logNotification({ kind: 'help', title: `${name} entrou para ajudar`, body: 'Num atendimento que está com você.', attendanceId: d.attendanceId });
      toast(`${name} entrou para ajudar num atendimento seu`, 'info', { label: 'Abrir', run: () => onOpenAttendance(d.attendanceId!) });
    },
    [qc, user?.id, onOpenAttendance],
  );
  const insightChanged = useCallback(() => void qc.invalidateQueries({ queryKey: ['insights'] }), [qc]);
  /** Cliente ficou irritado (análise da IA): só para quem acompanha a operação (supervisão). */
  const moodAlert = useCallback(
    (d: Data) => {
      if (!d.attendanceId || !hasPermission(user, 'report:read')) return;
      void qc.invalidateQueries({ queryKey: ['insights'] });
      const item = findCached(d.attendanceId)?.item;
      const who = item ? contactLabel(item) : 'Um cliente';
      const body = item?.operatorName ? `No atendimento com ${item.operatorName}.` : 'Ainda na fila.';
      playSound('alert');
      logNotification({ kind: 'mood', title: `${who} está irritado`, body, attendanceId: d.attendanceId });
      toast(`${who} está irritado (análise da IA)`, 'info', { label: 'Abrir', run: () => onOpenAttendance(d.attendanceId!) });
      desktopNotify(`${who} está irritado`, body, () => onOpenAttendance(d.attendanceId!), `mood-${d.attendanceId}`);
    },
    [qc, user, findCached, onOpenAttendance],
  );
  /** Fila parada: clientes esperando há mais que o limite configurado (só para a supervisão). */
  const queueWaitingLong = useCallback(
    (d: { attendanceIds?: unknown; minutes?: unknown }) => {
      const ids = Array.isArray(d.attendanceIds) ? d.attendanceIds.filter((x): x is string => typeof x === 'string') : [];
      if (!ids.length || !hasPermission(user, 'report:read')) return;
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      const minutes = Number(d.minutes) || 0;
      const first = findCached(ids[0])?.item;
      const title = ids.length === 1 ? `${first ? contactLabel(first) : 'Um cliente'} esperando na fila` : `${ids.length} clientes esperando na fila`;
      const body = `Há mais de ${minutes} min sem ninguém assumir.`;
      playSound('alert');
      logNotification({ kind: 'queue', title, body, attendanceId: ids.length === 1 ? ids[0] : null });
      toast(`${title} há mais de ${minutes} min`, 'info', { label: 'Abrir', run: () => onOpenAttendance(ids[0]) });
      desktopNotify(title, body, () => onOpenAttendance(ids[0]), `queue-wait-${ids.join(',')}`);
    },
    [qc, user, findCached, onOpenAttendance],
  );
  const mentioned = useCallback(
    (d: Data) => {
      if (d.by === user?.id || !d.attendanceId) return;
      playSound('alert');
      logNotification({ kind: 'mention', title: 'Você foi mencionado', body: 'Nota interna num atendimento.', attendanceId: d.attendanceId });
      toast('Você foi mencionado numa nota interna', 'info', { label: 'Abrir', run: () => onOpenAttendance(d.attendanceId!) });
      desktopNotify('Você foi mencionado', 'Nota interna num atendimento.', () => onOpenAttendance(d.attendanceId!));
    },
    [user?.id, onOpenAttendance],
  );
  const channelChanged = useCallback(
    (d: Data) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      if (d.status && d.status !== 'connected') {
        playSound('alert');
        logNotification({ kind: 'channel', title: `WhatsApp ${d.instance ?? ''} desconectado`.replace('  ', ' '), body: `Situação: ${d.status}` });
        toast(`WhatsApp ${d.instance ?? ''}: ${d.status}`, 'error');
      }
    },
    [qc],
  );

  const contactChanged = useCallback(
    (d: Data) => {
      void qc.invalidateQueries({ queryKey: ['contact', d.contactId] });
      // Nome e cliente aparecem na lista e no cabeçalho do atendimento.
      void qc.invalidateQueries({ queryKey: ['attendances'] });
      void qc.invalidateQueries({ queryKey: ['attendance'] });
    },
    [qc],
  );

  // Telefonia: ramal tocando (screen-pop) e mudanças da ligação. O aviso só traz o id: a tela relê.
  const callRinging = useCallback(
    (d: Data) => {
      void qc.invalidateQueries({ queryKey: ['calls', 'active'] });
      if (!d.callId) return;
      void get<CallView>(`/calls/${d.callId}`)
        .then((call) => {
          const who = callPartyLabel(call);
          const title = call.direction === 'internal' ? 'Ligação interna' : 'Ligação recebida';
          logNotification({ kind: 'call', title: `${title}: ${who}`, body: call.contact?.customer?.name ?? (call.phone ? 'Cliente' : ''), attendanceId: null });
          desktopNotify(title, who, () => window.focus(), `call-${call.id}`);
        })
        .catch(() => undefined);
    },
    [qc],
  );
  const callUpdated = useCallback(
    (d: Data) => {
      void qc.invalidateQueries({ queryKey: ['calls', 'active'] });
      void qc.invalidateQueries({ queryKey: ['call', d.callId] });
      void qc.invalidateQueries({ queryKey: ['calls', 'contact'] });
      if (d.state === 'ended') void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    [qc],
  );

  const callQueueChanged = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['calls', 'waiting'] });
    // Ligação que saiu da fila (atendida ou desistiu) muda a linha do tempo e o painel.
    void qc.invalidateQueries({ queryKey: ['calls', 'contact'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  }, [qc]);
  const agentStatusChanged = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['telephony', 'agents'] });
    void qc.invalidateQueries({ queryKey: ['telephony', 'me'] });
  }, [qc]);

  useSocketEvent('call.ringing', callRinging);
  useSocketEvent('call.queue', callQueueChanged);
  useSocketEvent('agent.status', agentStatusChanged);
  useSocketEvent('call.updated', callUpdated);
  useSocketEvent('attendance.changed', attendanceChanged);
  useSocketEvent('contact.changed', contactChanged);
  useSocketEvent('message.new', messageNew);
  useSocketEvent('contact.typing', contactTyping);
  useSocketEvent('message.status', messageChanged);
  useSocketEvent('message.updated', messageChanged);
  useSocketEvent('ticket.sync', syncChanged);
  useSocketEvent('presence.changed', presenceChanged);
  useSocketEvent('attendance.transferred_to_you', transferredToMe);
  useSocketEvent('note.mention', mentioned);
  useSocketEvent('session.started', voiceStarted);
  useSocketEvent('attendance.help_requested', helpRequested);
  useSocketEvent('attendance.helper_joined', helperJoined);
  useSocketEvent('channel.status', channelChanged);
  useSocketEvent('insight.updated', insightChanged);
  useSocketEvent('insight.mood', moodAlert);
  useSocketEvent('queue.waiting_long', queueWaitingLong);
  return null;
}
